import { Fragment, useMemo, useState } from "react";
import { mediaUrl, shuffleStable } from "../common";
import * as s from "./extra.styled";
import { useReport, useSlideState } from "../answers";

// Блоки уроку 2+ (урок 1 їх не використовує). Формат — FORMAT.md, «Доповнення v1.2».

// Порівняння відповіді. strict — з урахуванням регістру й . ? ! (коми ігноруємо): для вправ на великі літери й пунктуацію
const clean = (value = "", strict = false) => {
  const text = value.trim().replace(/[’']/g, "'").replace(/,/g, "").replace(/\s+/g, " ").replace(/\s+([.?!])/g, "$1");
  return strict ? text : text.toLowerCase().replace(/[.?!]+$/, "");
};
const matches = (value, answer, strict) => answer.split("|").some((option) => clean(option, strict) === clean(value, strict));

// **жирний** у тексті правил/табличок
export function Marked({ text }) {
  return String(text)
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) => (i % 2 ? <s.Mark key={i}>{part}</s.Mark> : <Fragment key={i}>{part}</Fragment>));
}

// Колонки: рядок { heading } починає нову колонку
const toColumns = (lines, columns) => {
  if (!columns || columns < 2) return [lines.map((line, i) => ({ ...line, i }))];
  const groups = [];
  lines.forEach((line, i) => (line.heading || !groups.length ? groups.push([{ ...line, i }]) : groups[groups.length - 1].push({ ...line, i })));
  return groups;
};

// ---------- choose ----------
// Рядок: «I'm from ((*France|French)).» — у ((…)) варіанти через |, правильний позначено *.
// Без ((…)) рядок просто показується (напр. репліка чату). Картинки зверху: data.images [{ url, label, credit }].
const CHOICE = /\(\(([^)]+)\)\)/g;

export function ChooseLines({ slideId, lines = [], columns, compact, dense, showAnswers, prefix = "" }) {
  const [picked, setPicked] = useSlideState(slideId, "picked", {});

  // Правильні варіанти кожного вибору — для рахунку в ДЗ
  const correctByKey = {};
  lines.forEach((line, i) =>
    String(line.text || "")
      .split(CHOICE)
      .forEach((part, j) => {
        if (j % 2) correctByKey[`${prefix}${i}-${j}`] = (part.split("|").find((o) => o.startsWith("*")) || "").slice(1);
      })
  );
  const keys = Object.keys(correctByKey);
  useReport(slideId, keys.filter((key) => picked[key] === correctByKey[key]).length, keys.length, keys.filter((key) => picked[key]).length);

  const renderLine = (line) => {
    if (line.heading) return <s.ChooseHeading key={`h${line.i}`}>{line.heading}</s.ChooseHeading>;
    const parts = String(line.text || "").split(CHOICE);
    return (
      <s.ChooseLine key={line.i}>
        {line.speaker && <s.Speaker>{line.speaker}:</s.Speaker>}
        <span>
          {parts.map((part, j) => {
            if (j % 2 === 0) return <Marked key={j} text={part} />;
            const key = `${prefix}${line.i}-${j}`;
            const options = part.split("|");
            return (
              <s.Options key={j}>
                {options.map((raw) => {
                  const correct = raw.startsWith("*");
                  const label = correct ? raw.slice(1) : raw;
                  const isPicked = picked[key] === label;
                  const state = showAnswers ? (correct ? "ok" : null) : isPicked ? (correct ? "ok" : "bad") : null;
                  return (
                    <s.Option key={raw} $picked={isPicked} $state={state} onClick={() => setPicked((prev) => ({ ...prev, [key]: label }))}>
                      {label}
                    </s.Option>
                  );
                })}
              </s.Options>
            );
          })}
        </span>
      </s.ChooseLine>
    );
  };

  const groups = toColumns(lines, columns);
  if (groups.length > 1) {
    return (
      <s.Columns $count={Math.min(groups.length, columns)}>
        {groups.map((group, g) => (
          <s.ChooseLines key={g} $compact={compact} $dense={dense}>
            {group.map(renderLine)}
          </s.ChooseLines>
        ))}
      </s.Columns>
    );
  }
  return (
    <s.ChooseLines $compact={compact} $dense={dense}>
      {groups[0].map(renderLine)}
    </s.ChooseLines>
  );
}

function Choose({ slide, showAnswers }) {
  const { lines = [], images = [], columns, compact, contain, dense } = slide.data || {};
  return (
    <s.ChooseWrap>
      {images.length > 0 && (
        <s.ChooseImages $count={images.length} $compact={compact}>
          {images.map((image) => (
            <s.ChooseImage key={image.url} $contain={contain}>
              <img src={mediaUrl(image.url)} alt="" />
              {image.label && <figcaption>{image.label}</figcaption>}
            </s.ChooseImage>
          ))}
        </s.ChooseImages>
      )}
      <ChooseLines slideId={slide.id} lines={lines} columns={columns} compact={compact || lines.length > 8} dense={dense} showAnswers={showAnswers} />
    </s.ChooseWrap>
  );
}

// ---------- video ----------
// data: { url, lines? (як у choose), start?, end? } — відео з керуванням і питання до нього.
// start/end — частина відео (напр. «перша частина» з книжки); вона вирізана в окремий файл, тож шкала коротка
function Video({ slide, showAnswers }) {
  const { url, lines = [], start, end } = slide.data || {};
  return (
    // Багато питань — вужче відео й компактні рядки, щоб усе влізло поруч
    <s.VideoLayout $wide={lines.length > 4}>
      <s.Video src={mediaUrl(url, { start, end })} controls preload="metadata" playsInline />
      <s.VideoSide>
        <ChooseLines slideId={slide.id} lines={lines} compact={lines.length > 4} showAnswers={showAnswers} />
      </s.VideoSide>
    </s.VideoLayout>
  );
}

// ---------- reading ----------
// data: { heading?, paragraphs: [string], lines (як у choose), images?: [{ url, label }], compact? }
// Текст ліворуч (**жирне** виділяється), питання праворуч — учень бачить текст, поки відповідає
function Reading({ slide, showAnswers }) {
  // images — маленькі фото з підписами (напр. A–D), якщо питання до тексту посилаються на них
  const { heading, paragraphs = [], lines = [], compact, images = [] } = slide.data || {};
  return (
    <s.ReadingLayout>
      <s.Article $compact={compact}>
        {heading && <h3>{heading}</h3>}
        {paragraphs.map((text, i) => (
          <p key={i}>
            <Marked text={text} />
          </p>
        ))}
      </s.Article>
      <s.ReadingSide>
        {images.length > 0 && (
          <s.ChooseImages $count={images.length} $thumbs>
            {images.map((image) => (
              <s.ChooseImage key={image.url}>
                <img src={mediaUrl(image.url)} alt="" />
                {image.label && <figcaption>{image.label}</figcaption>}
              </s.ChooseImage>
            ))}
          </s.ChooseImages>
        )}
        <ChooseLines slideId={slide.id} lines={lines} compact={compact || lines.length > 6} dense={lines.length > 6 || images.length > 0} showAnswers={showAnswers} />
      </s.ReadingSide>
    </s.ReadingLayout>
  );
}

// ---------- grammar ----------
// data: { tables: [{ title, rows: [[cell, …]] }], notes: [string], cols? } — **жирне** виділяється
function Grammar({ slide }) {
  const { tables = [], notes = [], cols = 2 } = slide.data || {};
  return (
    <s.GrammarLayout>
      <s.Tables $cols={cols}>
        {tables.map((table) => (
          <s.Table key={table.title}>
            <h3>{table.title}</h3>
            <table>
              <tbody>
                {table.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>
                        <Marked text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </s.Table>
        ))}
      </s.Tables>
      {notes.length > 0 && (
        <s.Notes>
          {notes.map((note) => (
            <s.Note key={note}>
              <Marked text={note} />
            </s.Note>
          ))}
        </s.Notes>
      )}
    </s.GrammarLayout>
  );
}

// ---------- unscramble ----------
// data: { items: [{ words: ["from", "I'm", "India"], end: ".", answer: "I'm from India." }] }
// Клік по слову — у речення; клік у реченні — назад. Перевірка, коли використані всі слова.
const sentenceOf = (item, built) => built.map((i) => item.words[i]).join(" ") + (item.end || "");

function ScrambleItem({ item, index, showAnswers, seed, built, setBuilt }) {
  const shuffled = useMemo(() => shuffleStable(item.words.map((word, i) => ({ word, i })), seed), [item.words, seed]);
  const done = built.length === item.words.length;
  const sentence = sentenceOf(item, built);
  const state = showAnswers ? "ok" : done ? (matches(sentence, item.answer, false) ? "ok" : "bad") : null;

  return (
    <s.ScrambleRow>
      <b>{index + 1}</b>
      <s.Built $state={state}>
        {showAnswers ? (
          item.answer.split("|")[0]
        ) : (
          <>
            {built.map((i, pos) => (
              <s.Word key={i} onClick={() => setBuilt(built.filter((x) => x !== i))}>
                {/* перше слово речення — з великої літери */}
                {pos === 0 ? item.words[i].charAt(0).toUpperCase() + item.words[i].slice(1) : item.words[i]}
              </s.Word>
            ))}
            {done && item.end && <s.End>{item.end}</s.End>}
          </>
        )}
      </s.Built>
      <s.Pool>
        {shuffled.map(({ word, i }) => (
          <s.Word key={i} $hidden={built.includes(i) || showAnswers} onClick={() => setBuilt([...built, i])}>
            {word}
          </s.Word>
        ))}
      </s.Pool>
    </s.ScrambleRow>
  );
}

function Unscramble({ slide, showAnswers }) {
  const { items = [] } = slide.data || {};
  const [built, setBuiltAll] = useSlideState(slide.id, "built", {});
  const done = (item, i) => (built[i] || []).length === item.words.length;
  useReport(
    slide.id,
    items.filter((item, i) => done(item, i) && matches(sentenceOf(item, built[i]), item.answer, false)).length,
    items.length,
    items.filter((item, i) => (built[i] || []).length > 0).length
  );
  return (
    <s.Scramble>
      {items.map((item, i) => (
        <ScrambleItem
          key={i}
          item={item}
          index={i}
          showAnswers={showAnswers}
          seed={`${slide.id}-${i}`}
          built={built[i] || []}
          setBuilt={(next) => setBuiltAll((prev) => ({ ...prev, [i]: next }))}
        />
      ))}
    </s.Scramble>
  );
}

// ---------- write ----------
// data: { lines: [{ speaker?, prompt, answer? }], strict? }
// answer є — перевірка після вводу (strict: регістр і . ? ! важливі); answer немає — вільне письмо, перевіряє вчитель.
function WriteLine({ line, strict, showAnswers, compact, value, setValue }) {
  const [typing, setTyping] = useState(false);
  const state = !line.answer ? null : showAnswers ? "ok" : value && !typing ? (matches(value, line.answer, strict) ? "ok" : "bad") : null;
  return (
    <s.WriteRow $speaker={Boolean(line.speaker)} $compact={compact}>
      {line.speaker && <s.Speaker>{line.speaker}:</s.Speaker>}
      <s.Prompt>
        <Marked text={line.prompt} />
      </s.Prompt>
      <s.WriteInput
        value={showAnswers && line.answer ? line.answer.split("|")[0] : value}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
        readOnly={showAnswers && Boolean(line.answer)}
        placeholder={line.placeholder ? `e.g. ${line.placeholder}` : ""}
        spellCheck={false}
        $state={state}
      />
    </s.WriteRow>
  );
}

function Write({ slide, showAnswers }) {
  const { lines = [], strict } = slide.data || {};
  const compact = lines.length > 6;
  const [values, setValues] = useSlideState(slide.id, "values", {});
  const checked = lines.map((line, i) => ({ line, i })).filter(({ line }) => line.answer);
  useReport(
    slide.id,
    checked.filter(({ line, i }) => values[i] && matches(values[i], line.answer, strict)).length,
    checked.length,
    Object.values(values).filter((v) => String(v).trim()).length
  );
  const list = (
    <s.WriteList $compact={compact}>
      {lines.map((line, i) => (
        <WriteLine
          key={i}
          line={line}
          strict={strict}
          showAnswers={showAnswers}
          compact={compact}
          value={values[i] || ""}
          setValue={(value) => setValues((prev) => ({ ...prev, [i]: value }))}
        />
      ))}
    </s.WriteList>
  );
  // slide.image — фото, яке учень описує (вчитель бачить те саме й може виправити)
  if (!slide.image?.url) return list;
  return (
    <s.WriteWithImage>
      <s.ChooseImage $contain={slide.image.fit === "contain"}>
        <img src={mediaUrl(slide.image.url)} alt="" />
      </s.ChooseImage>
      {list}
    </s.WriteWithImage>
  );
}

export const EXTRA_BLOCKS = {
  choose: Choose,
  reading: Reading,
  video: Video,
  grammar: Grammar,
  unscramble: Unscramble,
  write: Write,
};
